/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { debounce as _debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { CircularProgress, Portal } from '@mui/material';

import { useHUSpinStyles } from './HUSpin.styles';

export type HUSpinProps = {
  className?: string;
  containerClassName?: string;
  spinClassName?: string;
  spinning: boolean;
  children: React.ReactNode;
  indicator?: null | React.ReactNode;
  tip?: string | React.ReactNode;
  size?: number;
  delay?: number;
  enterDelay?: number;
  leaveDelay?: number;
  disableShrink?: boolean;
  indicatorPortal?: Element | (() => Element | null) | null;
};

export function HUSpin(props: HUSpinProps) {
  const {
    className,
    containerClassName,
    spinClassName,
    spinning,
    children,
    tip,
    indicator,
    size = 40,
    delay = 0,
    enterDelay = 0,
    leaveDelay = 0,
    indicatorPortal,
    disableShrink,
  } = props;
  const classes = useHUSpinStyles();
  const enterDelayRef = useRef<number>(enterDelay);
  const leaveDelayRef = useRef<number>(leaveDelay);
  const delayRef = useRef<number>(delay);
  const [innerSpinning, setSpinning] = useState<boolean>(spinning);

  const debouncedSetSpinning = useMemo(
    () => _debounce(() => setSpinning(true), enterDelayRef.current || delayRef.current || 0),
    [],
  );

  const debouncedRemoveSpinning = useMemo(
    () => _debounce(() => setSpinning(false), leaveDelayRef.current || delayRef.current || 0),
    [],
  );

  useEffect(() => {
    debouncedSetSpinning.cancel && debouncedSetSpinning.cancel();
    debouncedRemoveSpinning.cancel && debouncedRemoveSpinning.cancel();
    if (spinning) {
      debouncedSetSpinning();
    } else {
      debouncedRemoveSpinning();
    }
  }, [debouncedRemoveSpinning, debouncedSetSpinning, spinning]);

  const renderIndicator = () => {
    if (indicator === null) {
      return null;
    }
    if (React.isValidElement(indicator)) {
      return indicator;
    }
    return (
      <>
        <CircularProgress color="primary" size={size} disableShrink={disableShrink} />
        {tip && <div className={classes.tip}>{tip}</div>}
      </>
    );
  };

  return (
    <div
      className={cx(classes.root, className, {
        [classes.spinning]: innerSpinning,
      })}
      style={{ minWidth: `${size + 20}px`, minHeight: `${size + 20}px` }}
    >
      {innerSpinning && (
        <Portal disablePortal={!indicatorPortal} container={indicatorPortal}>
          <div className={cx(classes.spin, spinClassName)}>{renderIndicator()}</div>
        </Portal>
      )}
      <div className={cx(classes.container, containerClassName)}>{children}</div>
    </div>
  );
}
